import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from 'react';
import axios from "../../utils/axiosConfig";

const PortMaster = () => {

  const initialForm = {
    PortName: '',
    Province: '',
    Country: '',
    Remark: ''
  };

  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [ports, setPorts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  useEffect(() => {
    fetchPorts();
    fetchCountries();
  }, []);

  const fetchPorts = async () => {
    try {
      const res = await axios.get("/ports");
      setPorts(res.data);
    } catch (err) {
      alert("Failed to load ports");
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get("/countries");
      setCountries(res.data);
    } catch (err) {
      alert("Failed to load countries");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${"/ports"}/${editingId}`, form);
      } else {
        await axios.post("/ports", form);
      }

      alert(editingId ? "Port updated successfully" : "Port saved successfully");
      fetchPorts();
      resetForm();
      setActiveTab("list");
    } catch (err) {
      alert("Save failed: " + (err.response?.data || err.message));
    }
  };

  const handleEdit = (row) => {
    setForm({
      PortName: row.PortName || '',
      Province: row.Province || '',
      Country: row.Country || '',
      Remark: row.Remark || ''
    });
    setEditingId(row.PortID);
    setActiveTab("form");
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this port?")) return;

    try {
      await axios.delete(`${"/ports"}/${id}`);
      fetchPorts();
      if (editingId === id) resetForm();
    } catch (err) {
      alert("Delete failed");
    }
  };

  // Search filter
  const filteredPorts = ports.filter((row) => {
    const t = search.toLowerCase();
    if (!t) return true;
    return (
      (row.PortName || '').toLowerCase().includes(t) ||
      (row.Province || '').toLowerCase().includes(t) ||
      (row.CountryName || '').toLowerCase().includes(t)
    );
  });

  // Pagination
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentData = filteredPorts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPorts.length / rowsPerPage);

  return (
    <div className="container mt-3">

      <div className="mb-3">
        <h4 className="fw-bold">🛳️ Port Master</h4>
      </div>

      <div className="card shadow-sm border-0">

        {/* Tabs */}
        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}
              >
                Port List
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => {
                  resetForm();
                  setActiveTab("form");
                }}
              >
                {editingId ? "Edit Port" : "Add Port"}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body">

          {/* ================= LIST TAB ================= */}
          {activeTab === "list" && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Port Name</th>
                      <th>Province</th>
                      <th>Country</th>
                      <th>Remark</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-3">
                          No ports found.
                        </td>
                      </tr>
                    ) : (
                      currentData.map((row) => (
                        <tr key={row.PortID}>
                          <td>{row.PortID}</td>
                          <td>{row.PortName}</td>
                          <td>{row.Province}</td>
                          <td>{row.CountryName || row.Country}</td>
                          <td>{row.Remark}</td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(row)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(row.PortID)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {indexOfFirst + 1} to{" "}
                    {Math.min(indexOfLast, filteredPorts.length)} of{" "}
                    {filteredPorts.length} entries
                  </div>

                  <nav>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, index) => (
                        <li
                          key={index}
                          className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}

          {/* ================= FORM TAB ================= */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit}>
              <div className="row g-3">

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Port Name *</label>
                  <input
                    name="PortName"
                    value={form.PortName}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Province</label>
                  <input
                    name="Province"
                    value={form.Province}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Country</label>
                  <select
                    name="Country"
                    value={form.Country}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">-- Select Country --</option>
                    {countries.map(c => (
                      <option key={c.CountryId} value={c.CountryId}>
                        {c.CountryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">Remark</label>
                  <input
                    name="Remark"
                    value={form.Remark}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

              </div>

              <div className="mt-4 text-end">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab("list")}
                >
                  Back to List
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default PortMaster;
